import { ListJobOrderFragment } from 'shared/hooks/api/listJobOrderFragment/listJobOrderFragment.generated';
import { JobOrderListItem } from 'types/JobOrderListItem/JobOrderListItem';
import { mapDtoToJobOrderListItem } from 'types/JobOrderListItem/mappers';
import { create } from 'zustand';

interface JobOrdersStateType {
  recommended: JobOrderListItem[];
  other: JobOrderListItem[];
  activeApplied: JobOrderListItem[];
  endedApplied: JobOrderListItem[];
  externalOfferApplied: JobOrderListItem[];
  observed: JobOrderListItem[];
}

interface JobOrdersStoreType extends JobOrdersStateType {
  loadedLists: Record<keyof JobOrdersStateType, boolean>;
  loadListItems: (listName: keyof JobOrdersStateType, data: ListJobOrderFragment[]) => void;
  updateListItem: (id: string, payload: Partial<JobOrderListItem>) => void;
  moveListItem: (
    from: keyof JobOrdersStateType,
    to: keyof JobOrdersStateType,
    id: string,
    mapFn?: (arr: JobOrderListItem[]) => JobOrderListItem[],
  ) => void;
  copyListItem: (from: keyof JobOrdersStateType, to: keyof JobOrdersStateType, id: string) => void;
  removeListItem: (from: keyof JobOrdersStateType, id: string) => void;
  getListNamesById: (id: string) => (keyof JobOrdersStateType)[];
  getListNameById: (id: string) => keyof JobOrdersStateType | undefined;
  clearJobOrderLists: () => void;
}

const allListNames: (keyof JobOrdersStateType)[] = [
  'recommended',
  'other',
  'activeApplied',
  'endedApplied',
  'externalOfferApplied',
  'observed',
];
const getListNamesById = (state: JobOrdersStoreType, id: string) => {
  return allListNames.filter((listName) => state[listName].some((item) => item.id === id));
};

const getListNameById = (state: JobOrdersStoreType, id: string) =>
  allListNames.find((listName) => state[listName].some((item) => item.id === id));

const defaultLoadedLists = {
  recommended: false,
  other: false,
  activeApplied: false,
  endedApplied: false,
  externalOfferApplied: false,
  observed: false,
};

export const useJobOrdersStore = create<JobOrdersStoreType>((set, get) => ({
  loadedLists: defaultLoadedLists,
  recommended: [],
  other: [],
  activeApplied: [],
  endedApplied: [],
  externalOfferApplied: [],
  observed: [],
  loadListItems: (listName: keyof JobOrdersStateType, data: ListJobOrderFragment[]) =>
    set((prev) => ({
      loadedLists: {
        ...prev.loadedLists,
        [listName]: true,
      },
      [listName]: data.map(mapDtoToJobOrderListItem),
    })),
  updateListItem: (id: string, payload: Partial<JobOrderListItem>) => {
    set((prev) => {
      const listNames = getListNamesById(prev, id);
      if (!listNames.length) {
        return prev;
      }
      const updatedLists = listNames.reduce(
        (acc, listName) => {
          const prevList = prev[listName];
          acc[listName] = prevList.map((item) => (item.id === id ? { ...item, ...payload } : item));
          return acc;
        },
        {} as Record<keyof JobOrdersStateType, JobOrderListItem[]>,
      );
      return updatedLists;
    });
  },
  moveListItem: (
    from: keyof JobOrdersStateType,
    to: keyof JobOrdersStateType,
    id: string,
    mapFn?: (arr: JobOrderListItem[]) => JobOrderListItem[],
  ) =>
    set((prev) => {
      const targetItem = prev[from].find((item) => item.id === id);
      if (!targetItem) {
        return prev;
      }
      let toList = [...prev[to], targetItem];
      if (mapFn) {
        toList = mapFn(toList);
      }

      return {
        [from]: prev[from].filter((item) => item.id !== id),
        [to]: toList,
      };
    }),
  copyListItem: (from: keyof JobOrdersStateType, to: keyof JobOrdersStateType, id: string) =>
    set((prev) => {
      const targetItem = prev[from].find((item) => item.id === id);
      if (!targetItem) {
        return prev;
      }
      return {
        [to]: [targetItem, ...prev[to]],
      };
    }),
  removeListItem: (from: keyof JobOrdersStateType, id: string) =>
    set((prev) => {
      const targetItem = prev[from].find((item) => item.id === id);
      if (!targetItem) {
        return prev;
      }
      return {
        [from]: prev[from].filter((item) => item.id !== id),
      };
    }),
  getListNamesById: (id: string) => getListNamesById(get(), id),
  getListNameById: (id: string) => getListNameById(get(), id),
  clearJobOrderLists: () =>
    set({
      loadedLists: defaultLoadedLists,
      recommended: [],
      other: [],
      activeApplied: [],
      endedApplied: [],
      externalOfferApplied: [],
      observed: [],
    }),
}));
